import React, {useEffect, useState, useContext} from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import DonorToolPaymentTypeInfo, {IPaymentTypeInfo} from './DonorToolPaymentTypeInfo';
import DonationForm from "./DonationForm";
import Button from "tbk-components/src/components/Button";
import withAppContext from "tbk-components/src/hooks/withAppContext";
import Loading from "tbk-components/src/components/Loading/index";
import Modal from 'tbk-components/src/components/Modal';
import {handleGtmTags} from "../../GTMHandler";

export interface ICartItem {
	itemID?: string;
	image?: IImage;
	programCode?: string;
	donationHeading?: string;
	donationDescription?: string;
	donationText?: string;
	donationAmount?: number;
	donationAmountFormatted?: string;
	donationRecurringDuration?: string;
	donationType?: string;
	donationMessage?: string;
}

export interface ICartSummaryLineItem {
	label?: string;
	amount?: number;
}

export interface ICartSummary {
	title?: string;
	lineItems?: ICartSummaryLineItem[];
	totalLabel?: string;
	totalAmount?: number;
}

export interface IDonorToolCartProps extends IBaseElementProps {
	children?: any;
	cartHeading?: string;
	cartItems?: ICartItem[];
	cartSummary?: ICartSummary;
	paymentTypeInfo?: IPaymentTypeInfo;
	personalMessageIntro?: string;
	backToDonorToolURL?: string;
	billingDetailsURL?: string;
}

const DonorToolCart: React.FC<IDonorToolCartProps> = withAppContext( ({
	context,
	children,
	className,
	classNames = [],
	cartHeading,
	cartItems,
	cartSummary,
	paymentTypeInfo,
	personalMessageIntro,
	backToDonorToolURL,
	billingDetailsURL,
}) => {
	const { Api } = useContext( context );

const [cartItemForEdit, setCartItemForEdit] = useState({ itemID: '' } as ICartItem);
const [cartItemToRemove, setCartItemToRemove] = useState( { donationHeading: '', donationAmount: 0 });
const [errorMessage, setErrorMessage] = useState('');
const [refreshCartItemDisplay, setRefreshCartItemDisplay] = useState( false );
const [removeBusy, setRemoveBusy] = useState( [] );
const [showEditModal,setShowEditModal] = useState( false );
const [showRemoveModal,setShowRemoveModal] = useState( false );
const numberFormatter = Intl.NumberFormat('en-CA', {
	style: 'currency',
	currency: 'CAD'
});

const handleEdit = ( item ) => {
	if ( cartItemForEdit !== null && item.itemID != cartItemForEdit.itemID  ) {
		setRefreshCartItemDisplay(true);
	}
	if ( item.donationRecurringDuration ) {
		item.donationRecurringDuration = item.donationRecurringDuration.toLowerCase();
	}
	setCartItemForEdit( item );
	setShowEditModal( true );
};

const handleRemove = ( item ) => {
	setCartItemToRemove(item);
	setShowRemoveModal(true);
}

const processRemove = ( item ) => {
	setShowRemoveModal(false);
	setRemoveBusy( (prev) => [...prev, item.itemID] );
	Api.removeDonationFromCart( item ).then( results => {
		// refresh cart page
		handleGtmTags(results);
		window.location.href = results.url;
	}).catch( message => {
		setErrorMessage(message);
		setRemoveBusy([] );
	});
}

useEffect( () => {
	if ( cartItemForEdit !== null ) {
		setRefreshCartItemDisplay(false);
	}
}, [ cartItemForEdit ]);

	return (
	<div className={className || cn('donor-tool-cart', ...classNames)}>
		{errorMessage &&
		<div className="error-message">{errorMessage}</div>
		}
		<div className="container-fluid">
			<div className="donor-tool-cart__top row">
				<div className="col-12">
					{cartHeading && (
						<h1 className="donor-tool-cart__heading">
							{cartHeading}
						</h1>
					)}
				</div>
			</div>
			<div className="donor-tool-cart__bottom row">
				<div className="donor-tool-cart__left col-12 col-lg-8">
					<div className="donor-tool-cart__items">
						{( cartItems || [] ).map((item, index) => (
							<div className="donor-tool-cart__item" key={index}>
								<div className="donor-tool-cart__item-left">
									<div className="donor-tool-cart__item-image">
										{item.image && (
											<Image
												className="img-fluid"
												{...item.image}
											/>
										)}
									</div>
									<div className="donor-tool-cart__item-content">
										{item.programCode && (
											<p className="donor-tool-cart__item-donation-code">
												{item.programCode}
											</p>
										)}
										{item.donationHeading && (
											<h3 className="donor-tool-cart__item-heading">
												{item.donationHeading}
											</h3>
										)}
										{item.donationDescription && (
											<p className="donor-tool-cart__item-description" dangerouslySetInnerHTML={ {__html: item.donationDescription } }>
											</p>
										)}
										{item.donationText && (
											<p className="donor-tool-cart__item-text">
												{item.donationText}
											</p>
										)}
										{item.donationMessage && (
											<p className="donor-tool-cart__item-message">
												<span className="donor-tool-cart__item-message-intro">
													{personalMessageIntro && (
														personalMessageIntro + ':'
													)}
												</span><br />{item.donationMessage}
											</p>
										)}
									</div>
								</div>
								<div className="donor-tool-cart__item-right">
									<div className="donor-tool-cart__item-amount-container">
										{item.donationAmountFormatted && (
											<p className="donor-tool-cart__item-amount">
												{item.donationAmountFormatted}
											</p>
										)}
										{item.donationRecurringDuration && (
											<p className="donor-tool-cart__item-recurring-duration">
												{ //keep the formatting of 'Semi-Annually'
													item.donationRecurringDuration.split('-').map( (word) => {
														return word[0].toUpperCase() + word.substring(1);
													}).join('-')
												}
											</p>
										)}
									</div>
									<div className="donor-tool-cart__item-edit-options">
										<Button
											data-bs-toggle="modal"
											data-bs-target={'#donation-edit-form'}
											className="donor-tool-cart__item-edit-link"
											onClick={ () => handleEdit(item) }
										>
											Edit
										</Button>
										{ removeBusy.indexOf(item.itemID) == -1 &&
											<Button
												data-bs-toggle="modal"
												data-bs-target={'#donation-remove-from-cart'}
												className="donor-tool-cart__item-remove-link"
												onClick={() => handleRemove(item)}
											>
												<i className="icon icon-close"></i>
											</Button>
										}
										{ removeBusy.indexOf(item.itemID) !== -1 &&
											<Loading inline={true}/>
										}

									</div>
								</div>
							</div>
						))}
					</div>
					<a
						href={backToDonorToolURL}
						className="donor-tool-cart__add-more-donations btn btn-lg btn-primary"
					>
						<i className="icon icon-chevron-left"></i>
						{' '}Add More Donations
					</a>
					<a
						href={billingDetailsURL}
						className="donor-tool-cart__billing-details btn btn-lg btn-primary"
					>
						Checkout{' '}
						<i className="icon icon-chevron-right"></i>
					</a>
				</div>
				<div className="donor-tool-cart__right col-12 col-lg-4">
					<div className="donor-tool-cart__summary">
						<h3 className="donor-tool-cart__summary-heading">
							{cartSummary.title}
						</h3>
						<div className="donor-tool-cart__summary-items">
							{( cartSummary.lineItems || [] ).map((item, index) => (
							<div className="donor-tool-cart__summary-item">
								<p className="donor-tool-cart__summary-item-label">
									{item.label}
								</p>
								<p className="donor-tool-cart__summary-item-amount">
									{item.amount}
								</p>
							</div>
							))}
							<div className="donor-tool-cart__summary-items-total">
								<p className="donor-tool-cart__summary-items-total-label">
									{cartSummary.totalLabel}
								</p>
								<p className="donor-tool-cart__summary-items-total-amount">
									{cartSummary.totalAmount}
								</p>
							</div>
						</div>
					</div>
					<a
						href={backToDonorToolURL}
						className="donor-tool-cart__add-more-donations btn btn-lg btn-primary"
					>
						<i className="icon icon-chevron-left"></i>
						{' '}Add More Donations
					</a>
					<a
						href={billingDetailsURL}
						className="donor-tool-cart__billing-details btn btn-primary"
					>
						Checkout{' '}
						<i className="icon icon-chevron-right"></i>
					</a>
					<div className="donor-tool-cart__moneris-info">
						<DonorToolPaymentTypeInfo paymentTypeInfo={paymentTypeInfo} />
					</div>
				</div>
			</div>
		</div>
		{ ( showEditModal && !refreshCartItemDisplay && cartItemForEdit ) && <Modal
			id="donation-edit-form"
			className="donation-form-modal"
			modalClassNames={['modal-dialog-centered']}
			headerClassNames={[]}
			bodyClassNames={[]}
			onCancel={() => setShowEditModal(false)}
		>
			<DonationForm initialItem={cartItemForEdit} paymentTypeInfo={paymentTypeInfo} />
		</Modal>}
		{ ( showRemoveModal && cartItemToRemove ) && <Modal
			id="donation-remove-from-cart"
			className="donation-form-modal"
			modalClassNames={['modal-dialog-centered']}
			headerClassNames={[]}
			bodyClassNames={[]}
			onCancel={() => setShowRemoveModal(false)}
		>
			<div className="modal-remove-message">
				<p>Are you sure you want to remove "{cartItemToRemove?.donationHeading}" (${cartItemToRemove?.donationAmount}) from the cart?</p>
			</div>

			<div className="modal-remove-buttons">
				<Button
					className="btn btn-primary btn-md"
					onClick={() => processRemove(cartItemToRemove)}
				>
					YES
				</Button>
				<Button
					className="btn btn-secondary btn-md"
					aria-label="Close"
					onClick={() => setShowRemoveModal(false)}
				>NO</Button>
			</div>
		</Modal>}
	</div>
)});

export default DonorToolCart;
